import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { GraduationCap, TrendingUp, Wallet, BarChart3 } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const PALETTE = [
  "#234333",
  "#d4af37",
  "#b07d2c",
  "#3a5a40",
  "#9e2a2b",
  "#6b655b",
  "#1b3326",
  "#8a6d1d",
  "#5b8c5a",
  "#7d2122",
  "#a4623a",
  "#4a4a4a",
];

const fmt = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

const Tabs = ({ tabs, value, onChange }) => (
  <div className="flex flex-wrap gap-1 mb-6 border-b border-[#e5e0d8]">
    {tabs.map((t) => (
      <button
        key={t.key}
        onClick={() => onChange(t.key)}
        data-testid={`acad-tab-${t.key}`}
        className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
          value === t.key
            ? "border-[#234333] text-[#234333] font-medium"
            : "border-transparent text-[#6b655b] hover:text-[#1c1a17]"
        }`}
      >
        <t.icon size={14} strokeWidth={1.5} />
        {t.label}
      </button>
    ))}
  </div>
);

export default function AcademicReports() {
  const [tab, setTab] = useState("departments");
  const [departments, setDepartments] = useState([]);
  const [intake, setIntake] = useState({ institutes: [], rows: [] });
  const [fees, setFees] = useState({ institutes: [], rows: [] });
  const [intakeList, setIntakeList] = useState([]);
  const [feesList, setFeesList] = useState([]);
  const [fys, setFys] = useState([]);
  const [filterFy, setFilterFy] = useState("");
  const [filterInstitute, setFilterInstitute] = useState("");
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [d, ic, fc, fyr, insts] = await Promise.all([
          api.get("/academic/departments"),
          api.get("/academic/intake-comparison"),
          api.get("/academic/fees-comparison"),
          api.get("/financial-years"),
          api.get("/institutes"),
        ]);
        setDepartments(d.data);
        setIntake(ic.data);
        setFees(fc.data);
        setFys(fyr.data);
        setInstitutes(insts.data);
        const cur = (fyr.data || []).find((f) => f.is_current);
        if (cur) setFilterFy(cur.id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Reload detail lists when filter changes
  useEffect(() => {
    (async () => {
      const params = {};
      if (filterFy) params.fy_id = filterFy;
      if (filterInstitute) params.institute_id = filterInstitute;
      const [il, fl] = await Promise.all([
        api.get("/academic/intake", { params }),
        api.get("/academic/fees", { params }),
      ]);
      setIntakeList(il.data);
      setFeesList(fl.data);
    })();
  }, [filterFy, filterInstitute]);

  // Chart data: only show last ~10 FYs by default for readability
  const intakeChart = useMemo(
    () => intake.rows.slice(-10),
    [intake.rows],
  );
  const feesChart = useMemo(
    () => fees.rows.slice(-10),
    [fees.rows],
  );

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="academic-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">
          Trust Reports
        </div>
        <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Academic Comparison</h1>
        <p className="mt-2 text-sm text-[#6b655b] max-w-3xl">
          Cross-institute view of departments, sanctioned intake & approved fees — recorded by
          each institute under their portal and aggregated here for the trust office.
        </p>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "departments", label: "Departments", icon: GraduationCap },
          { key: "intake", label: "Intake", icon: TrendingUp },
          { key: "fees", label: "Fees", icon: Wallet },
        ]}
      />

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : tab === "departments" ? (
        <div
          data-testid="acad-departments"
          className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {["Institute", "Department", "Code", "Course", "Approval", "HoD", "Active"].map(
                  (h, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {departments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-12 text-center text-sm text-[#6b655b]">
                    No departments recorded by institutes yet.
                  </td>
                </tr>
              ) : (
                departments.map((d) => (
                  <tr key={d.id} className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60">
                    <td className="px-4 py-4 text-[#1c1a17]">{d.institute_name || "—"}</td>
                    <td className="px-4 py-4 text-[#1c1a17]">{d.name}</td>
                    <td className="px-4 py-4 font-mono text-xs text-[#6b655b]">
                      {d.code || "—"}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b]">{d.course_type || "—"}</td>
                    <td className="px-4 py-4 text-[#6b655b]">{d.approval_body || "—"}</td>
                    <td className="px-4 py-4 text-[#6b655b]">{d.head_of_department || "—"}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`inline-block w-2 h-2 rounded-full ${
                          d.is_active ? "bg-[#3a5a40]" : "bg-[#9e2a2b]"
                        }`}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : tab === "intake" ? (
        <div data-testid="acad-intake" className="space-y-8">
          {/* Chart */}
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={14} strokeWidth={1.5} className="text-[#234333]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Sanctioned intake by institute · last 10 FYs
              </div>
            </div>
            {intakeChart.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#6b655b]">
                No intake data yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={intakeChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d8" />
                  <XAxis dataKey="fy_label" stroke="#6b655b" fontSize={11} />
                  <YAxis stroke="#6b655b" fontSize={11} />
                  <Tooltip
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e5e0d8",
                      borderRadius: 2,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {intake.institutes.map((name, i) => (
                    <Bar
                      key={name}
                      dataKey={name}
                      fill={PALETTE[i % PALETTE.length]}
                      stackId="a"
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Filters + table */}
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Financial Year
              </div>
              <select
                data-testid="acad-intake-fy-filter"
                value={filterFy}
                onChange={(e) => setFilterFy(e.target.value)}
                className={inputCls + " w-56"}
              >
                <option value="">All FYs</option>
                {fys.map((f) => (
                  <option key={f.id} value={f.id}>
                    FY {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Institute
              </div>
              <select
                data-testid="acad-intake-inst-filter"
                value={filterInstitute}
                onChange={(e) => setFilterInstitute(e.target.value)}
                className={inputCls + " w-72"}
              >
                <option value="">All institutes</option>
                {institutes.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="acad-intake-table">
              <thead>
                <tr className="bg-[#f9f8f6] text-left">
                  {["FY", "Institute", "Department", "Sanctioned", "Admitted", "Vacant", "Approval Ref"].map((h, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {intakeList.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="p-12 text-center text-sm text-[#6b655b]">
                      No intake records for the selected filters.
                    </td>
                  </tr>
                ) : (
                  intakeList.map((r) => (
                    <tr key={r.id} className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60">
                      <td className="px-4 py-4 font-mono text-xs">{r.financial_year_label}</td>
                      <td className="px-4 py-4 text-[#1c1a17]">{r.institute_name}</td>
                      <td className="px-4 py-4 text-[#6b655b]">{r.department_name}</td>
                      <td className="px-4 py-4 tabular-nums">{r.sanctioned_intake}</td>
                      <td className="px-4 py-4 tabular-nums">{r.actual_admitted}</td>
                      <td className="px-4 py-4 tabular-nums text-[#6b655b]">{r.vacant_seats}</td>
                      <td className="px-4 py-4 text-[#6b655b]">{r.approval_reference || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div data-testid="acad-fees" className="space-y-8">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={14} strokeWidth={1.5} className="text-[#234333]" />
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">
                Average annual fee per institute · last 10 FYs (₹)
              </div>
            </div>
            {feesChart.length === 0 ? (
              <div className="py-12 text-center text-sm text-[#6b655b]">No fees data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={feesChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e0d8" />
                  <XAxis dataKey="fy_label" stroke="#6b655b" fontSize={11} />
                  <YAxis
                    stroke="#6b655b"
                    fontSize={11}
                    tickFormatter={(v) => fmt(v)}
                  />
                  <Tooltip
                    formatter={(v) => `₹ ${fmt(v)}`}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #e5e0d8",
                      borderRadius: 2,
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {fees.institutes.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Financial Year
              </div>
              <select
                data-testid="acad-fees-fy-filter"
                value={filterFy}
                onChange={(e) => setFilterFy(e.target.value)}
                className={inputCls + " w-56"}
              >
                <option value="">All FYs</option>
                {fys.map((f) => (
                  <option key={f.id} value={f.id}>
                    FY {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Institute
              </div>
              <select
                data-testid="acad-fees-inst-filter"
                value={filterInstitute}
                onChange={(e) => setFilterInstitute(e.target.value)}
                className={inputCls + " w-72"}
              >
                <option value="">All institutes</option>
                {institutes.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="acad-fees-table">
              <thead>
                <tr className="bg-[#f9f8f6] text-left">
                  {[
                    "FY",
                    "Institute",
                    "Department",
                    "Tuition",
                    "Devt",
                    "Exam",
                    "Other",
                    "Total ₹",
                    "Authority",
                  ].map((h, i) => (
                    <th
                      key={i}
                      className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {feesList.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="p-12 text-center text-sm text-[#6b655b]">
                      No fees records for the selected filters.
                    </td>
                  </tr>
                ) : (
                  feesList.map((r) => (
                    <tr key={r.id} className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60">
                      <td className="px-4 py-4 font-mono text-xs">{r.financial_year_label}</td>
                      <td className="px-4 py-4 text-[#1c1a17]">{r.institute_name}</td>
                      <td className="px-4 py-4 text-[#6b655b]">{r.department_name}</td>
                      <td className="px-4 py-4 tabular-nums">{fmt(r.tuition_fee)}</td>
                      <td className="px-4 py-4 tabular-nums">{fmt(r.development_fee)}</td>
                      <td className="px-4 py-4 tabular-nums">{fmt(r.exam_fee)}</td>
                      <td className="px-4 py-4 tabular-nums text-[#6b655b]">
                        {fmt(r.other_charges)}
                      </td>
                      <td className="px-4 py-4 text-[#1c1a17] tabular-nums font-medium">
                        ₹ {fmt(r.total_annual_fee)}
                      </td>
                      <td className="px-4 py-4 text-[#6b655b]">{r.fees_approval_authority || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
